import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  AdjustmentsHorizontalIcon,
  BeakerIcon,
  BoltIcon,
  ChartBarSquareIcon,
  CommandLineIcon,
  CpuChipIcon,
  MagnifyingGlassCircleIcon,
  PresentationChartLineIcon,
} from '@heroicons/react/24/outline'

import { Badge, Button } from '../ui'
import { cn } from '../../shared/lib/cn'

type ShellSectionId =
  | 'dashboard'
  | 'lens'
  | 'testing'
  | 'evals'
  | 'telemetry'
  | 'prompts'
  | 'gepa'
  | 'tiny'

export interface AIConsoleShellSummaryItem {
  label: string
  value: React.ReactNode
  detail: string
  tone?: 'default' | 'brand' | 'success' | 'warning' | 'danger'
}

interface AIConsoleShellProps {
  sectionId: ShellSectionId
  title: string
  description: string
  plainLanguageSummary: string
  actions?: React.ReactNode
  summaryItems?: AIConsoleShellSummaryItem[]
}

const SECTION_META: Array<{
  id: ShellSectionId
  path: string
  label: string
  caption: string
  group: string
  icon: React.ComponentType<React.ComponentProps<'svg'>>
}> = [
  {
    id: 'dashboard',
    path: '/ai-console/dashboard',
    label: 'Overview',
    caption: 'See health, spend, and where attention is needed.',
    group: 'Start here',
    icon: PresentationChartLineIcon,
  },
  {
    id: 'lens',
    path: '/ai-console/agent-lens',
    label: 'Inspect Runs',
    caption: 'Open one output and trace how it was produced.',
    group: 'Investigate',
    icon: MagnifyingGlassCircleIcon,
  },
  {
    id: 'testing',
    path: '/ai-console/agent-testing',
    label: 'Try Changes',
    caption: 'Test prompt or model ideas in a safe scratchpad.',
    group: 'Experiment',
    icon: BeakerIcon,
  },
  {
    id: 'evals',
    path: '/evals',
    label: 'Evaluate',
    caption: 'Measure quality on a saved dataset before promotion.',
    group: 'Validate',
    icon: ChartBarSquareIcon,
  },
  {
    id: 'telemetry',
    path: '/telemetry',
    label: 'Watch Live Traffic',
    caption: 'Connect real requests to the traces behind them.',
    group: 'Observe',
    icon: BoltIcon,
  },
  {
    id: 'prompts',
    path: '/prompt-management',
    label: 'Manage Prompts',
    caption: 'Review versions and the prompts currently in use.',
    group: 'Manage',
    icon: AdjustmentsHorizontalIcon,
  },
  {
    id: 'gepa',
    path: '/gepa-optimizer',
    label: 'Optimize Prompts',
    caption: 'Search for stronger prompt variants and compare them.',
    group: 'Optimize',
    icon: CommandLineIcon,
  },
  {
    id: 'tiny',
    path: '/ai-console/tiny-model-studio',
    label: 'Tiny Models',
    caption: 'Track lightweight models once a workflow is stable.',
    group: 'Ship',
    icon: CpuChipIcon,
  },
]

const TONE_STYLES: Record<NonNullable<AIConsoleShellSummaryItem['tone']>, string> = {
  default:
    'border-gray-200 bg-white/85 text-charcoal dark:border-dark-border/60 dark:bg-dark-surface/80 dark:text-gray-100',
  brand:
    'border-rose-200 bg-rose-50/90 text-rose-900 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-100',
  success:
    'border-emerald-200 bg-emerald-50/90 text-emerald-900 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-100',
  warning:
    'border-amber-200 bg-amber-50/90 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-100',
  danger:
    'border-rose-200 bg-rose-50/90 text-rose-900 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-100',
}

export function AIConsoleShell({
  sectionId,
  title,
  description,
  plainLanguageSummary,
  actions,
  summaryItems = [],
}: AIConsoleShellProps) {
  const navigate = useNavigate()
  const location = useLocation()

  return (
    <section className="relative overflow-hidden rounded-[32px] border border-rose-200/80 bg-[linear-gradient(135deg,rgba(255,248,246,0.98),rgba(255,255,255,0.92))] shadow-[0_1px_0_rgba(255,255,255,0.65)_inset,0_18px_44px_-30px_rgba(146,44,44,0.34),0_36px_90px_-64px_rgba(146,44,44,0.38)] dark:border-rose-950/40 dark:bg-[linear-gradient(135deg,rgba(34,24,24,0.98),rgba(17,17,19,0.96))] dark:shadow-[0_1px_0_rgba(255,255,255,0.05)_inset,0_24px_64px_-40px_rgba(0,0,0,0.55)]">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(224,115,92,0.18),transparent_34%),radial-gradient(circle_at_bottom_left,rgba(120,39,39,0.12),transparent_30%)]" />

      <div className="relative grid gap-6 px-6 py-6 lg:grid-cols-[minmax(0,1.25fr),minmax(280px,0.9fr)] lg:px-8 lg:py-8">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="brand">AI Console</Badge>
            <Badge variant="secondary">
              {SECTION_META.find((section) => section.id === sectionId)?.group ?? 'Workspace'}
            </Badge>
          </div>

          <h1
            className="mt-4 max-w-3xl font-title text-[clamp(2rem,4vw,3.2rem)] leading-[1.02] tracking-[-0.03em] text-charcoal dark:text-gray-50"
            style={{ textWrap: 'balance' }}
          >
            {title}
          </h1>

          <p
            className="mt-3 max-w-2xl text-base leading-7 text-stone-700 dark:text-stone-300"
            style={{ textWrap: 'pretty' }}
          >
            {description}
          </p>

          <div className="mt-5 max-w-2xl rounded-[24px] border border-stone-200/80 bg-white/70 px-5 py-4 shadow-[0_1px_0_rgba(255,255,255,0.9)_inset,0_16px_36px_-30px_rgba(43,20,32,0.28)] dark:border-dark-border/60 dark:bg-dark-surface/60 dark:shadow-[0_1px_0_rgba(255,255,255,0.04)_inset,0_18px_44px_-34px_rgba(0,0,0,0.45)]">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-stone-500 dark:text-stone-400">
              In Plain Language
            </p>
            <p
              className="mt-2 text-sm leading-6 text-stone-700 dark:text-stone-300"
              style={{ textWrap: 'pretty' }}
            >
              {plainLanguageSummary}
            </p>
          </div>

          {actions && <div className="mt-5 flex flex-wrap items-center gap-2">{actions}</div>}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          {summaryItems.map((item) => (
            <div
              key={item.label}
              className={cn(
                'min-h-[132px] rounded-[24px] border px-5 py-4 backdrop-blur-sm shadow-[0_1px_0_rgba(255,255,255,0.72)_inset,0_14px_32px_-28px_rgba(43,20,32,0.24)] transition-[transform,box-shadow,border-color,background-color] duration-150 ease-[cubic-bezier(0.2,0,0,1)] hover:-translate-y-0.5 hover:shadow-[0_1px_0_rgba(255,255,255,0.85)_inset,0_18px_38px_-26px_rgba(43,20,32,0.28)]',
                TONE_STYLES[item.tone ?? 'default'],
              )}
            >
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-current/65">
                {item.label}
              </p>
              <div className="mt-2 text-lg font-semibold leading-tight text-current tabular-nums">{item.value}</div>
              <p className="mt-2 text-sm leading-5 text-current/75" style={{ textWrap: 'pretty' }}>
                {item.detail}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="relative border-t border-stone-200/80 px-4 py-4 dark:border-dark-border/60 lg:px-6">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-stone-500 dark:text-stone-400">
              Workflow Map
            </p>
            <p className="mt-1 text-sm text-stone-600 dark:text-stone-300" style={{ textWrap: 'pretty' }}>
              Move from understanding the system to testing, validation, and prompt operations without losing context.
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/ai-console/dashboard')}>
            Reset to Overview
          </Button>
        </div>

        <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4">
          {SECTION_META.map((section) => {
            const isActive = location.pathname === section.path
            const Icon = section.icon
            return (
              <button
                key={section.id}
                type="button"
                onClick={() => navigate(section.path)}
                className={cn(
                  'group min-h-[144px] rounded-[22px] border px-4 py-4 text-left transition-[transform,border-color,background-color,box-shadow] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 will-change-transform',
                  isActive
                    ? 'border-rose-300 bg-rose-50/90 shadow-[0_1px_0_rgba(255,255,255,0.82)_inset,0_18px_40px_-28px_rgba(146,44,44,0.5)] dark:border-rose-900/60 dark:bg-rose-950/35'
                    : 'border-stone-200/90 bg-white/70 shadow-[0_1px_0_rgba(255,255,255,0.7)_inset,0_16px_32px_-28px_rgba(43,20,32,0.18)] hover:border-stone-300 hover:bg-white hover:shadow-[0_1px_0_rgba(255,255,255,0.82)_inset,0_22px_42px_-28px_rgba(43,20,32,0.24)] dark:border-dark-border/60 dark:bg-dark-surface/55 dark:hover:border-dark-border dark:hover:bg-dark-surface/85',
                )}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-[18px] bg-stone-100 text-stone-700 shadow-[0_1px_0_rgba(255,255,255,0.88)_inset,0_10px_24px_-18px_rgba(43,20,32,0.32)] transition-[transform,opacity,filter,background-color] duration-200 ease-[cubic-bezier(0.2,0,0,1)] group-hover:scale-[1.02] dark:bg-dark-surface-2 dark:text-stone-200">
                    <Icon className="h-4 w-4" />
                  </div>
                  <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-500 dark:text-stone-400">
                    {section.group}
                  </span>
                </div>
                <p className="mt-3 text-sm font-semibold text-charcoal dark:text-gray-100">{section.label}</p>
                <p className="mt-1 text-sm leading-5 text-stone-600 dark:text-stone-300" style={{ textWrap: 'pretty' }}>
                  {section.caption}
                </p>
              </button>
            )
          })}
        </div>
      </div>
    </section>
  )
}
